import { AuditMixinAttributes, AuditMixinRelationships } from "./audit-mixin";
import { DEFAULT, LIST_RELATIONSHIP_TYPE } from "./shared-types";

export abstract class LockPolicyRule {
    abstract type: string;
}

export const AuditAttributes = [
    "created_on",
    "changed_on",
    // "changed_by_name", // we cannot actually query by this and it can't be locked so... ignore it
] as const;
type AuditAttribute = (typeof AuditAttributes)[number];

export const ComponentAttributes = [
    "start_time",
    "end_time",
    "name",
    "description",
] as const;
type ComponentAttribute = (typeof ComponentAttributes)[number];

export const EventAttributes = [
    "start",
    "end",
    "duration",
] as const;
type EventAttribute = (typeof EventAttributes)[number];

const LockableAttributes = [...new Set([
    ...ComponentAttributes,
    ...EventAttributes,
])] as const;


export class ComponentAttributeValueRule extends LockPolicyRule {
    type: "component_attribute_value" = "component_attribute_value";
    attribute: ComponentAttribute | AuditAttribute;
    operator: string;
    value: any;
}

export class EventAttributeValueRule extends LockPolicyRule {
    type: "event_attribute_value" = "event_attribute_value";
    attribute: EventAttribute | AuditAttribute;
    operator: string;
    value: any;
}

export class AttributeValueRule extends LockPolicyRule {
    type: "attribute_value" = "attribute_value";
    attribute: AuditAttribute;
    operator: string;
    value: any;
}

export class ComponentTypeRule extends LockPolicyRule {
    type: "component_type" = "component_type";
    operator: "==" | "!=" = "==";
    component_type_id: string;
}

export class EventTypeRule extends LockPolicyRule {
    type: "event_type" = "event_type";
    operator: "==" | "!=" = "==";
    event_type_id: string;
}

export class CPValueRule extends LockPolicyRule {
    type: "cp_value" = "cp_value";
    cp_id: string;
    operator: string;
    value: any;
}

export class CompoundRule extends LockPolicyRule {
    type: "compound" = "compound";
    operator: "and" | "or";
    rules: LockPolicyRule[];
}

export type LockdownRule =
    | ComponentAttributeValueRule
    | EventAttributeValueRule
    | AttributeValueRule
    | ComponentTypeRule
    | EventTypeRule
    | CPValueRule
    | CompoundRule;

export interface ComponentTypeExclusion {
    component_type_id: string;
    constant_property_ids: string[];
}

export interface EventTypeExclusion {
    event_type_id: string;
    constant_property_ids: string[];
}

export class LockPolicyAttributes extends AuditMixinAttributes {
    name: string;
    enabled: boolean = true;
    rule: LockdownRule | null = {
        type: "compound",
        operator: "and",
        rules: [],
    } satisfies CompoundRule;
    schedule: string = "0 * * * *"; // Default: every hour
    last_run_on?: string | null;
    attributes_to_lock: string[] = [...LockableAttributes];
    exclusions: (ComponentTypeExclusion | EventTypeExclusion)[] = [];
}

export class LockPolicyRelationships extends AuditMixinRelationships {
    lock_templates: LIST_RELATIONSHIP_TYPE = DEFAULT.LIST;
}

export class LockPolicy {
    attributes: LockPolicyAttributes;
    relationships: LockPolicyRelationships;
    id: string;
    type: "lock_policy";

    constructor() {
        this.type = "lock_policy";
        this.attributes = new LockPolicyAttributes();
        this.relationships = new LockPolicyRelationships();
    }
}

export class LockPolicyRuleValidation {
    affected: {
        component_count: number;
        component_type_ids: string[];
        event_count: number;
        event_type_ids: string[];
        cp_count: number;
        cp_ids: string[];
    };
    contradiction?: {
        path: string;
        reason: string;
    };
}

export interface JobSummary {
    id: number;
    status?: string;
    created_on?: string;
    completed_on?: string;
}

export interface LockPolicyRunData {
    component_type_count: number;
    event_type_count: number;
    cp_count: number;
    component_count: number;
    event_count: number;
    jobs_created: number;
    job_ids: number[];
}

export class LockPolicyRun {
    id: string;
    policy_id: string;
    start_time: string;
    end_time?: string | null;
    data?: LockPolicyRunData;
    type: "lock_policy_run";

    constructor() {
        this.type = "lock_policy_run";
    }
}
